import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { Figure } from 'src/app/shared/models/main/figure'
import { Note } from 'src/app/shared/models/main/note'
import { Quiz } from 'src/app/shared/models/main/quiz'
import { Dictionary } from '../../shared/models/main/dictionary'
import { Pair } from '../../shared/models/main/pair'
import { Word } from '../../shared/models/main/word'
import { AuthUserService } from '../auth/auth-user.service'
import { DictionaryService } from '../data/dictionary.service'
import { FigureService } from '../data/figure.service'
import { NoteService } from '../data/note.service'
import { PairService } from '../data/pair.service'
import { QuizService } from '../data/quiz.service'
import { WordService } from '../data/word.service'
import { UserService } from '../user-info/user.service'

export interface ApiResponse<T> {
  results: T[]
  count: number
  detail?: string
}

export type ContentTypeKey = 'words' | 'pairs' | 'dictionaries' | 'notes' | 'quizzes' | 'figures'

interface ContentTypeState<T = any> {
  items: T[]
  currentPage: number
  totalItems: number
  loading: boolean
}

type LibraryState = Record<ContentTypeKey, ContentTypeState>

@Injectable({
  providedIn: 'root',
})
export class LibraryStateService {
  private readonly ITEMS_PER_PAGE = 10

  private _username: string = undefined
  private state: LibraryState = {
    words: { items: [], currentPage: 1, totalItems: 0, loading: false },
    pairs: { items: [], currentPage: 1, totalItems: 0, loading: false },
    dictionaries: { items: [], currentPage: 1, totalItems: 0, loading: false },
    notes: { items: [], currentPage: 1, totalItems: 0, loading: false },
    quizzes: { items: [], currentPage: 1, totalItems: 0, loading: false },
    figures: { items: [], currentPage: 1, totalItems: 0, loading: false },
  }

  private stateSubject = new BehaviorSubject<LibraryState>(this.state)
  private destroy$ = new Subject<void>()
  private fetchFns: Record<ContentTypeKey, (page: number, pageSize: number) => Observable<any>>

  constructor(
    private wordService: WordService,
    private pairService: PairService,
    private dictionaryService: DictionaryService,
    private noteService: NoteService,
    private quizService: QuizService,
    private figureService: FigureService,
    private auth_user: AuthUserService,
    private user_info: UserService,
  ) {
    this.fetchFns = {
      words: (page: number, pageSize: number): Observable<any> =>
        this.wordService.get_user_words_page(this._username, page, pageSize),
      pairs: (page: number, pageSize: number): Observable<any> =>
        this.pairService.get_user_pairs_page(this._username, page, pageSize),
      dictionaries: (page: number, pageSize: number): Observable<any> =>
        this.dictionaryService.get_user_dics_page(this._username, page, pageSize),
      notes: (page: number, pageSize: number): Observable<any> =>
        this.noteService.get_user_notes_page(this._username, page, pageSize),
      quizzes: (page: number, pageSize: number): Observable<any> =>
        this.quizService.get_quizzes_page(this._username, page, pageSize),
      figures: (page: number, pageSize: number): Observable<any> =>
        this.figureService.get_figures_page(this._username, page, pageSize),
    }

    this.auth_user
      .is_logged_in()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res.has_valid_tokens) {
          this.user_info.userInfo$.pipe(takeUntil(this.destroy$)).subscribe((userInfo: any) => {
            this._username = userInfo.username
          })
        }
      })
  }

  // Generic selectors
  getItems$<T>(key: ContentTypeKey): Observable<T[]> {
    return this.stateSubject.pipe(map(state => state[key].items as T[]))
  }

  getLoading$(key: ContentTypeKey): Observable<boolean> {
    return this.stateSubject.pipe(map(state => state[key].loading))
  }

  get totalItems$(): Observable<Record<ContentTypeKey, number>> {
    return this.stateSubject.pipe(
      map(state => {
        const totals = {} as Record<ContentTypeKey, number>
        for (const key of Object.keys(state) as ContentTypeKey[]) {
          totals[key] = state[key].totalItems
        }
        return totals
      }),
    )
  }

  // Backward-compatible selectors
  get words$(): Observable<Word[]> {
    return this.getItems$<Word>('words')
  }
  get pairs$(): Observable<Pair[]> {
    return this.getItems$<Pair>('pairs')
  }
  get dictionaries$(): Observable<Dictionary[]> {
    return this.getItems$<Dictionary>('dictionaries')
  }
  get notes$(): Observable<Note[]> {
    return this.getItems$<Note>('notes')
  }
  get quizzes$(): Observable<Quiz[]> {
    return this.getItems$<Quiz>('quizzes')
  }
  get figures$(): Observable<Figure[]> {
    return this.getItems$<Figure>('figures')
  }
  get wordsLoading$(): Observable<boolean> {
    return this.getLoading$('words')
  }
  get pairsLoading$(): Observable<boolean> {
    return this.getLoading$('pairs')
  }
  get dictionariesLoading$(): Observable<boolean> {
    return this.getLoading$('dictionaries')
  }
  get notesLoading$(): Observable<boolean> {
    return this.getLoading$('notes')
  }
  get quizzesLoading$(): Observable<boolean> {
    return this.getLoading$('quizzes')
  }
  get figuresLoading$(): Observable<boolean> {
    return this.getLoading$('figures')
  }

  // Generic actions
  async loadItems(key: ContentTypeKey, reset: boolean = false): Promise<void> {
    if (reset) {
      this.updateContentState(key, { items: [], currentPage: 1 })
    }

    this.updateContentState(key, { loading: true })

    try {
      const currentPage = this.state[key].currentPage
      const response = (await firstValueFrom(
        this.fetchFns[key](currentPage, this.ITEMS_PER_PAGE),
      )) as ApiResponse<any>

      if (response && response.results) {
        this.updateContentState(key, {
          items: reset ? response.results : [...this.state[key].items, ...response.results],
          totalItems: response.count || 0,
          loading: false,
        })
      } else {
        this.updateContentState(key, { loading: false })
      }
    } catch (error) {
      this.updateContentState(key, { loading: false })
      throw error
    }
  }

  loadMoreItems(key: ContentTypeKey): Promise<void> | undefined {
    if (!this.state[key].loading) {
      this.updateContentState(key, {
        currentPage: this.state[key].currentPage + 1,
      })
      return this.loadItems(key)
    }
    return undefined
  }

  addItem(key: ContentTypeKey, item: any): void {
    this.updateContentState(key, {
      items: [item, ...this.state[key].items],
      totalItems: this.state[key].totalItems + 1,
    })
  }

  updateItem(key: ContentTypeKey, item: any): void {
    const updatedItems = this.state[key].items.map((i: any) => (i.id === item.id ? item : i))
    this.updateContentState(key, { items: updatedItems })
  }

  // Backward-compatible load methods
  async loadWords(reset = false): Promise<void> {
    return this.loadItems('words', reset)
  }
  async loadPairs(reset = false): Promise<void> {
    return this.loadItems('pairs', reset)
  }
  async loadDictionaries(reset = false): Promise<void> {
    return this.loadItems('dictionaries', reset)
  }
  async loadNotes(reset = false): Promise<void> {
    return this.loadItems('notes', reset)
  }
  async loadQuizzes(reset = false): Promise<void> {
    return this.loadItems('quizzes', reset)
  }
  async loadFigures(reset = false): Promise<void> {
    return this.loadItems('figures', reset)
  }

  // Backward-compatible loadMore methods
  loadMoreWords(): Promise<void> | undefined {
    return this.loadMoreItems('words')
  }
  loadMorePairs(): Promise<void> | undefined {
    return this.loadMoreItems('pairs')
  }
  loadMoreDictionaries(): Promise<void> | undefined {
    return this.loadMoreItems('dictionaries')
  }
  loadMoreNotes(): Promise<void> | undefined {
    return this.loadMoreItems('notes')
  }
  loadMoreQuizzes(): Promise<void> | undefined {
    return this.loadMoreItems('quizzes')
  }
  loadMoreFigures(): Promise<void> | undefined {
    return this.loadMoreItems('figures')
  }

  // Backward-compatible add methods
  addWord(word: Word): void {
    this.addItem('words', word)
  }
  addPair(pair: Pair): void {
    this.addItem('pairs', pair)
  }
  addDictionary(dictionary: Dictionary): void {
    this.addItem('dictionaries', dictionary)
  }
  addNote(note: Note): void {
    this.addItem('notes', note)
  }
  addQuiz(quiz: Quiz): void {
    this.addItem('quizzes', quiz)
  }
  addFigure(figure: Figure): void {
    this.addItem('figures', figure)
  }

  // Backward-compatible update methods
  updateWord(word: Word): void {
    this.updateItem('words', word)
  }
  updatePair(pair: Pair): void {
    this.updateItem('pairs', pair)
  }
  updateDictionary(dictionary: Dictionary): void {
    this.updateItem('dictionaries', dictionary)
  }
  updateNote(note: Note): void {
    this.updateItem('notes', note)
  }
  updateQuiz(quiz: Quiz): void {
    this.updateItem('quizzes', quiz)
  }
  updateFigure(figure: Figure): void {
    this.updateItem('figures', figure)
  }

  updateTotalCounts(
    wordCount: number,
    pairCount: number,
    dictionaryCount: number,
    noteCount: number,
    quizCount: number,
    figureCount: number,
  ): void {
    const counts: Record<ContentTypeKey, number> = {
      words: wordCount,
      pairs: pairCount,
      dictionaries: dictionaryCount,
      notes: noteCount,
      quizzes: quizCount,
      figures: figureCount,
    }
    for (const key of Object.keys(counts) as ContentTypeKey[]) {
      this.state = {
        ...this.state,
        [key]: { ...this.state[key], totalItems: counts[key] },
      }
    }
    this.stateSubject.next(this.state)
  }

  private updateContentState(key: ContentTypeKey, partial: Partial<ContentTypeState>): void {
    this.state = {
      ...this.state,
      [key]: { ...this.state[key], ...partial },
    }
    this.stateSubject.next(this.state)
  }
}
